import { useState, useEffect, useRef } from 'react';
import { locationService } from '../services/locationService';
import { validateDateInput, validateVisaDuration } from '../utils/dateValidation';

export const useVisaApplication = ({
    onDateChange,
    onDurationChange,
    onCountryChange,
    onConfirm,
    onReset,
    selectedDate,
    visaDuration,
    selectedCountry,
    allErrors = {},
    showOnboarding = false
}) => {
    // UI State
    const [showCalendar, setShowCalendar] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingStep, setProcessingStep] = useState(null); // 'validating', 'calculating', 'finalizing'
    const [showPreferencesPopup, setShowPreferencesPopup] = useState(false);
    const [userPreferences, setUserPreferences] = useState(null);
    const [showDurationPopup, setShowDurationPopup] = useState(false);
    const [showCountryPopup, setShowCountryPopup] = useState(false);
    const [localErrors, setLocalErrors] = useState({});
    const [showLocationTooltip, setShowLocationTooltip] = useState(false);

    // Location State
    const [userIP, setUserIP] = useState(null);
    const [userCity, setUserCity] = useState(null);
    const [userCountryName, setUserCountryName] = useState(null);
    const [userCountryCode, setUserCountryCode] = useState(null);
    const [userTimezone, setUserTimezone] = useState(null);
    const [detectedCountry, setDetectedCountry] = useState('');
    const [detectedCountryCode, setDetectedCountryCode] = useState('');

    // Language State
    const [language, setLanguage] = useState(() => {
        return localStorage.getItem('appLanguage') || 'en';
    });

    // Persist language preference
    useEffect(() => {
        localStorage.setItem('appLanguage', language);
    }, [language]);

    // Haptic Feedback Helper
    const triggerHaptic = () => {
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
            navigator.vibrate(50);
        }
    };

    // Fetch user's IP address and location data
    useEffect(() => {
        const fetchUserLocation = async () => {
            try {
                const locationData = await locationService.getLocationData();
                console.log('Location data received in useVisaApplication:', locationData);
                if (locationData) {
                    // Always set IP if available, even if other data is missing
                    if (locationData.ip) {
                        setUserIP(locationData.ip);
                        console.log('Set userIP:', locationData.ip);
                    }
                    if (locationData.city) {
                        setUserCity(locationData.city);
                        console.log('Set userCity:', locationData.city);
                    }
                    if (locationData.country) {
                        setUserCountryName(locationData.country);
                        setDetectedCountry(locationData.country);
                        console.log('Set userCountryName:', locationData.country);
                    }
                    if (locationData.countryCode) {
                        // Ensure countryCode is uppercase
                        const upperCode = locationData.countryCode.toUpperCase();
                        setUserCountryCode(upperCode);
                        setDetectedCountryCode(upperCode);
                        console.log('Set userCountryCode:', upperCode);
                    }
                    if (locationData.timezone) setUserTimezone(locationData.timezone);
                } else {
                    console.warn('No location data received from service');
                }
            } catch (error) {
                console.error('Shared location service failed:', error.message);
            }
        };

        fetchUserLocation();
    }, []);

    // Check for user preferences on component mount
    useEffect(() => {
        const savedPreferences = localStorage.getItem('userPreferences');
        if (savedPreferences) {
            setUserPreferences(JSON.parse(savedPreferences));
        }
    }, []);

    // Handlers
    const handleOpenPreferences = () => setShowPreferencesPopup(true);
    const handlePreferencesClose = () => setShowPreferencesPopup(false);
    const handlePreferencesConfirm = (preferences) => {
        setUserPreferences(preferences);
        setShowPreferencesPopup(false);
    };

    const handleCalendarClose = () => {
        setShowCalendar(false);
    };

    const handleConfirmDateSelection = () => {
        if (selectedDate) {
            const dateValue = selectedDate instanceof Date
                ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
                : selectedDate;
            onDateChange(dateValue);
        }
        setShowCalendar(false);
    };

    const handleDurationPopupClose = () => {
        setShowDurationPopup(false);
    };

    const handleCountryPopupClose = () => {
        setShowCountryPopup(false);
    };

    // Track previous values to detect when a selection was just confirmed
    const prevSelectedCountry = useRef(null);
    const prevSelectedDate = useRef(null);
    const prevVisaDuration = useRef(null);

    // Reset refs when values are cleared
    useEffect(() => {
        if (!selectedCountry) {
            prevSelectedCountry.current = null;
        }
        if (!selectedDate) {
            prevSelectedDate.current = null;
        }
        if (!visaDuration) {
            prevVisaDuration.current = null;
        }
    }, [selectedCountry, selectedDate, visaDuration]);

    // Track country selection for haptics
    useEffect(() => {
        const countryJustSelected = selectedCountry && !prevSelectedCountry.current && !selectedDate;
        if (countryJustSelected) {
            triggerHaptic();
            prevSelectedCountry.current = selectedCountry;
        } else if (selectedCountry) {
            prevSelectedCountry.current = selectedCountry;
        }
    }, [selectedCountry, selectedDate]);

    // Track date selection for haptics
    useEffect(() => {
        const dateJustSelected = selectedDate && !prevSelectedDate.current && !visaDuration;
        if (dateJustSelected) {
            triggerHaptic();
            prevSelectedDate.current = selectedDate;
        } else if (selectedDate) {
            prevSelectedDate.current = selectedDate;
        }
    }, [selectedDate, visaDuration]);

    // Haptic on duration complete
    useEffect(() => {
        const durationJustSelected = visaDuration && !prevVisaDuration.current;
        if (durationJustSelected) {
            triggerHaptic();
            prevVisaDuration.current = visaDuration;
        } else if (visaDuration) {
            prevVisaDuration.current = visaDuration;
        }
    }, [visaDuration]);

    // Prevent background scrolling when any modal or overlay is open
    useEffect(() => {
        const anyPopupOpen =
            showCalendar ||
            showPreferencesPopup ||
            showDurationPopup ||
            showCountryPopup ||
            isProcessing ||
            showOnboarding;

        if (anyPopupOpen) {
            // Store scroll position for iOS
            const scrollY = window.scrollY;
            
            // Lock body scroll
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = 'var(--scrollbar-width, 0px)';
            
            // Prevent iOS bounce scrolling
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = `-${scrollY}px`;
        } else {
            // Restore scroll position for iOS
            const scrollY = document.body.style.top;
            
            // Restore body styles
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
            
            // Restore scroll position
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        }

        return () => {
            // Cleanup on unmount
            const scrollY = document.body.style.top;
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        };
    }, [showCalendar, showPreferencesPopup, showDurationPopup, showCountryPopup, isProcessing, showOnboarding]);

    const handleActualSubmit = () => {
        // Validate before submitting
        const newErrors = {};
        const dateValidation = validateDateInput(selectedDate);
        if (!dateValidation.isValid) newErrors.date = dateValidation.error;

        const durationValidation = validateVisaDuration(visaDuration);
        if (!durationValidation.isValid) newErrors.duration = durationValidation.error;

        if (Object.keys(newErrors).length > 0) {
            setLocalErrors(newErrors);
            return;
        }
        setLocalErrors({});

        setIsProcessing(true);
        setProcessingStep('validating');

        setTimeout(() => {
            setProcessingStep('calculating');
        }, 1000);

        setTimeout(() => {
            setProcessingStep('finalizing');
        }, 2000);

        setTimeout(() => {
            onConfirm();
            setIsProcessing(false);
            setProcessingStep(null);
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
        }, 3000);
    };

    const handleEditCountry = () => {
        setShowCountryPopup(true);
    };

    const handleEditDate = () => {
        setShowCalendar(true);
    };

    const handleEditDuration = () => {
        setShowDurationPopup(true);
    };

    // Step Helpers
    const getCurrentStep = () => {
        if (!selectedCountry) return 'country';
        if (!selectedDate) return 'date';
        if (!visaDuration) return 'duration';
        return 'confirm';
    };

    const getCompletedSteps = () => {
        const completed = [];
        if (selectedCountry) completed.push('country');
        if (selectedDate) completed.push('date');
        if (visaDuration) completed.push('duration');
        if (isProcessing) completed.push('confirm');
        return completed;
    };

    return {
        state: {
            showCalendar,
            isProcessing,
            processingStep,
            showPreferencesPopup,
            userPreferences,
            showDurationPopup,
            // showConfirmationPopup - Removed
            // isEditMode - Removed
            showCountryPopup,
            localErrors,
            showLocationTooltip,
            location: {
                userIP,
                userCity,
                userCountryName,
                userCountryCode,
                userTimezone,
                detectedCountry,
                detectedCountryCode
            },
            currentStep: getCurrentStep(),
            completedSteps: getCompletedSteps(),
            language
        },
        actions: {
            setShowCalendar,
            setShowDurationPopup,
            setShowCountryPopup,
            setShowLocationTooltip,
            handleOpenPreferences,
            handlePreferencesClose,
            handlePreferencesConfirm,
            handleCalendarClose,
            handleConfirmDateSelection,
            handleDurationPopupClose,
            handleCountryPopupClose,
            handleConfirmVisaCheck: handleActualSubmit,
            handleEditCountry,
            handleEditDate,
            handleEditDuration,
            setLanguage,
        }
    };
};
